import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  MenuItem, Button, Autocomplete, IconButton, Stack, Typography, InputAdornment,
} from '@mui/material';
import { SaveOutlined, AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import {
  P, PX, cardSx, fieldSx, gridInput, priBtn, outBtn, WorkspaceHeader, TotalsStrip, ValidationStrip,
} from '../../components/common/workspaceKit';
import { productService, Product } from '../../services/productService';
import { inventoryService, Warehouse } from '../../services/inventoryService';

interface Line { key: number; productId: number | null; warehouseId: number | null; quantity: number; rate: number; }
let SEQ = 1;
const newLine = (): Line => ({ key: SEQ++, productId: null, warehouseId: null, quantity: 0, rate: 0 });
const money = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const OpeningStockFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [lines, setLines] = useState<Line[]>([newLine()]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    productService.getProducts({ limit: 500 } as any).then((r) => setProducts(r.products || [])).catch(() => {});
    inventoryService.getWarehouses().then((r) => {
      const ws = r.warehouses || [];
      setWarehouses(ws);
      if (ws.length) setLines((ls) => ls.map((l) => (l.warehouseId ? l : { ...l, warehouseId: ws[0].id })));
    }).catch(() => {});
  }, []);

  const setLine = (key: number, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { ...newLine(), warehouseId: warehouses[0]?.id ?? null }]);
  const removeLine = (key: number) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : ls));

  const valid = useMemo(() => lines.filter((l) => l.productId && l.warehouseId && Number(l.quantity) > 0 && Number(l.rate) >= 0), [lines]);
  const totalQty = useMemo(() => valid.reduce((s, l) => s + Number(l.quantity || 0), 0), [valid]);
  const totalValue = useMemo(() => valid.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.rate || 0), 0), [valid]);

  const validations = [
    { ok: valid.length > 0, label: `${valid.length} valid line(s)` },
    { ok: lines.every((l) => !l.productId || l.warehouseId), label: 'Warehouse set on every line' },
  ];
  const canSave = valid.length > 0 && validations.every((v) => v.ok);

  const submit = async () => {
    if (!canSave) return;
    setSaving(true); setError(null);
    try {
      await inventoryService.recordOpeningStock({
        lines: valid.map((l) => ({ productId: l.productId as number, warehouseId: l.warehouseId as number, quantity: Number(l.quantity), rate: Number(l.rate) })),
        notes: notes || undefined,
      });
      navigate('/inventory/stock');
    } catch (e: any) {
      setError(e?.message || 'Failed to record opening stock');
      setSaving(false);
    }
  };

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100%' }}>
      <WorkspaceHeader
        onBack={() => navigate('/inventory')}
        title="Opening Stock"
        subtitle="Load opening quantity + rate per item — posts Dr Inventory / Cr Opening Balance Equity and seeds the cost layers."
        actions={<Button variant="contained" startIcon={<SaveOutlined />} sx={priBtn} disabled={!canSave || saving} onClick={submit}>{saving ? 'Posting…' : 'Post Opening Stock'}</Button>}
      />

      <ValidationStrip items={validations} />

      <Box sx={{ px: PX, pb: 4 }}>
        {error && <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2.5, bgcolor: P.redBg, color: P.red, fontWeight: 600, fontSize: 13 }}>{error}</Paper>}

        <Box sx={{ mb: 2.5 }}>
          <TotalsStrip cells={[
            { label: 'Valid Lines', value: valid.length },
            { label: 'Total Quantity', value: totalQty.toLocaleString('en-IN') },
            { label: 'Total Value', value: money(totalValue), tone: 'primary' },
          ]} />
        </Box>

        <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden', mb: 2.5 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Product', 'Warehouse', 'Quantity', 'Rate', 'Value', ''].map((h) => (
                    <TableCell key={h} sx={{ bgcolor: P.head, fontWeight: 700, fontSize: '11px', letterSpacing: '.03em', textTransform: 'uppercase', color: '#64748b' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l) => (
                  <TableRow key={l.key}>
                    <TableCell sx={{ minWidth: 260 }}>
                      <Autocomplete
                        size="small"
                        options={products}
                        getOptionLabel={(o) => `${o.product_code} — ${o.product_name}`}
                        value={products.find((p) => Number(p.id) === Number(l.productId)) || null}
                        onChange={(_, v) => setLine(l.key, { productId: v ? Number(v.id) : null, rate: l.rate || Number(v?.purchase_price || 0) })}
                        renderInput={(params) => <TextField {...params} sx={gridInput} placeholder="Select product" />}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <TextField select size="small" fullWidth sx={gridInput} value={l.warehouseId ?? ''} onChange={(e) => setLine(l.key, { warehouseId: Number(e.target.value) })}>
                        {warehouses.map((w) => <MenuItem key={w.id} value={w.id}>{w.warehouseName}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ width: 120 }}>
                      <TextField type="number" size="small" fullWidth sx={gridInput} value={l.quantity} onChange={(e) => setLine(l.key, { quantity: Number(e.target.value) })} />
                    </TableCell>
                    <TableCell sx={{ width: 140 }}>
                      <TextField type="number" size="small" fullWidth sx={gridInput} value={l.rate} onChange={(e) => setLine(l.key, { rate: Number(e.target.value) })}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                    </TableCell>
                    <TableCell sx={{ width: 140 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{money(Number(l.quantity || 0) * Number(l.rate || 0))}</Typography>
                    </TableCell>
                    <TableCell sx={{ width: 48 }}>
                      <IconButton size="small" onClick={() => removeLine(l.key)} disabled={lines.length === 1}><DeleteOutline fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ p: 1.5, borderTop: `1px solid ${P.line}` }}>
            <Button startIcon={<AddCircleOutline />} onClick={addLine} sx={{ textTransform: 'none', fontWeight: 600 }}>Add line</Button>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ ...cardSx, p: PX, mb: 2.5 }}>
          <TextField fullWidth size="small" sx={fieldSx} label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} multiline minRows={2} />
        </Paper>

        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <Button variant="outlined" sx={outBtn} onClick={() => navigate('/inventory')}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveOutlined />} sx={priBtn} disabled={!canSave || saving} onClick={submit}>{saving ? 'Posting…' : 'Post Opening Stock'}</Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default OpeningStockFormPage;
